import type { Board, ChannelRow, CostInput, MarginCalc, LeadRow, MarginStatus, MonthlyChannelRow, MonthlyPeriod, Opportunity, Overview, Readout, WeeklyChannelRow, WeeklyPeriod } from "./api";

export const GAP_MARK = "–";
export type NeedsYouRow = { kind: "overdue_leads" | "unreplied_leads" | "follow_up_overdue_leads" | "gates_waiting" | "blocked_margin_calcs" | "pending_tree_proposals"; count: number; label: string; tab?: "leads" | "board"; action?: string };
export type NeedsYouView = { state: "error" } | { state: "quiet" } | { state: "rows"; rows: NeedsYouRow[] };
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
export function assembleNeedsYou(overview: Overview | undefined, error: boolean): NeedsYouView {
  if (error || !overview) return { state: "error" };
  const n = overview.needs_you; const rows: NeedsYouRow[] = [];
  if (n.overdue_leads > 0) rows.push({ kind: "overdue_leads", count: n.overdue_leads, label: plural(n.overdue_leads, "overdue lead", "overdue leads"), tab: "leads", action: "Open leads" });
  if (n.unreplied_leads > n.overdue_leads) rows.push({ kind: "unreplied_leads", count: n.unreplied_leads, label: plural(n.unreplied_leads, "unreplied lead", "unreplied leads"), tab: "leads", action: "Open leads" });
  if (n.follow_up_overdue_leads > 0) rows.push({ kind: "follow_up_overdue_leads", count: n.follow_up_overdue_leads, label: plural(n.follow_up_overdue_leads, "lead past its follow-up date", "leads past their follow-up date"), tab: "leads", action: "Open leads" });
  if (n.gates_waiting > 0) rows.push({ kind: "gates_waiting", count: n.gates_waiting, label: plural(n.gates_waiting, "margin gate waiting on you", "margin gates waiting on you"), tab: "board", action: "Open the board" });
  if (n.blocked_margin_calcs > 0) rows.push({ kind: "blocked_margin_calcs", count: n.blocked_margin_calcs, label: plural(n.blocked_margin_calcs, "blocked margin calculation", "blocked margin calculations"), tab: "board", action: "Open the board" });
  if (n.pending_tree_proposals > 0) rows.push({ kind: "pending_tree_proposals", count: n.pending_tree_proposals, label: plural(n.pending_tree_proposals, "pending category proposal", "pending category proposals") });
  return rows.length ? { state: "rows", rows } : { state: "quiet" };
}
export type InFlightView = { state: "ready"; count: number; cap: number; full: boolean } | { state: "denied" | "gap" | "loading"; mark: string; label: string };
export function inFlightCounter(overview: Overview | undefined, denied: boolean, loading = false): InFlightView {
  if (denied) return { state: "denied", mark: GAP_MARK, label: "No access" };
  const value = overview?.in_flight;
  if (!value || !Number.isFinite(value.count) || !Number.isFinite(value.cap)) return loading ? { state: "loading", mark: GAP_MARK, label: "Loading" } : { state: "gap", mark: GAP_MARK, label: "No data" };
  return { state: "ready", count: value.count, cap: value.cap, full: value.count >= value.cap };
}
export const COUNTER_BOARD_MISMATCH_MESSAGE = "Partial state: the overview counter and board currently disagree. Retry before making a capacity decision.";
export function selectToRunGuard(counter: InFlightView, names: string[], disagree = false): { disabled: boolean; message: string | null } {
  if (counter.state === "denied") return { disabled: true, message: "Your sign-in cannot read the in-flight counter, so starting a run is locked. Ask the Customer Acquisition area owner." };
  if (counter.state === "loading") return { disabled: true, message: null };
  if (counter.state !== "ready") return { disabled: true, message: "The in-flight counter is unavailable, so starting a run is locked." };
  if (disagree) return { disabled: true, message: COUNTER_BOARD_MISMATCH_MESSAGE };
  if (!counter.full) return { disabled: false, message: null };
  return { disabled: true, message: `${counter.cap} channels are already in flight. Park one to start another: currently ${names.length ? names.join(", ") : "names unavailable"}. Open the board.` };
}
export function counterBoardMismatch(overview: Overview | undefined, board: Board | undefined): boolean {
  if (!overview || !board || !Number.isFinite(overview.in_flight?.count) || !Array.isArray(board.columns)) return false;
  // The cap is counted on root branches (funnel spec c2-store-roots-root-cap): two countries under
  // one channel are one slot, so the board's row count is compared to in_flight.opportunities,
  // the per-row figure the API now carries. Older API builds carry only count; fall back to it.
  const rows = Number.isFinite(overview.in_flight.opportunities) ? (overview.in_flight.opportunities as number) : overview.in_flight.count;
  return board.columns.reduce((sum, column) => sum + (column.items ?? []).filter((item) => item.in_flight).length, 0) !== rows;
}
export type FunnelColumn = { key: "qualify" | "prepare" | "margin-gate" | "activate" | "live"; label: string; items: Opportunity[] };
export function boardColumns(board: Board | undefined): FunnelColumn[] {
  const items = (phase: string) => board?.columns.find((column) => column.phase === phase)?.items ?? [];
  const prepare = items("prepare");
  const atGate = prepare.filter((item) => item.margin_status === "pending" || item.margin_status === "blocked_pending_inputs");
  const gateIds = new Set(atGate.map((item) => item.opportunity_id));
  const columns: FunnelColumn[] = [
    { key: "qualify", label: "Qualify", items: items("qualify") },
    { key: "prepare", label: "Prepare", items: prepare.filter((item) => !gateIds.has(item.opportunity_id)) },
    { key: "margin-gate", label: "Margin gate", items: atGate },
    { key: "activate", label: "Activate", items: items("activate") },
    { key: "live", label: "Live", items: items("live") },
  ];
  const seen = new Set<string>();
  return columns.map((column) => ({ ...column, items: column.items.filter((item) => {
    if (seen.has(item.opportunity_id)) return false;
    seen.add(item.opportunity_id);
    return true;
  }) }));
}
/** The channel type an opportunity belongs to: the ROOT of its branch path (C2 joins paths with " › "). */
export function channelType(item: Pick<Opportunity, "branch_path" | "node_name">): string | null {
  const root = item.branch_path?.split("›")[0]?.trim();
  return root || item.node_name?.trim() || null;
}
/** Channel-type options offered on the board: only the types the board's own items carry, so a
 *  dropped or test row can never put a type on the list (meeting 2026-09-21, "focus on 1-3 channels"). */
export function boardChannelTypes(columns: FunnelColumn[]): Array<{ value: string; label: string }> {
  const types = new Set<string>();
  for (const column of columns) for (const item of column.items) { const type = channelType(item); if (type) types.add(type); }
  return [...types].sort((a, b) => a.localeCompare(b)).map((value) => ({ value, label: value }));
}
/** Narrow the board to one channel type, client-side. No type means the whole board. */
export function filterBoardColumns(columns: FunnelColumn[], type: string | null | undefined): FunnelColumn[] {
  if (!type) return columns;
  return columns.map((column) => ({ ...column, items: column.items.filter((item) => channelType(item) === type) }));
}
export const NEXT_PHASE: Record<string, "qualify" | "prepare" | "activate" | "live" | null> = { backlog: "qualify", qualify: "prepare", prepare: "activate", activate: "live", live: null };
export type GapCell = { mark: string; label: string; value?: string | number };
export function gapCell(value: string | number | null | undefined, label = "No data"): GapCell {
  return value === null || value === undefined || value === "" || (typeof value === "number" && !Number.isFinite(value)) ? { mark: GAP_MARK, label } : { mark: String(value), label: "", value };
}
/** A recorded fact is whatever JSON the writer sent (C2 record-fact stores the value as-is), so it
 *  can be an object or an array, not only a scalar. String() on an object is "[object Object]" (bug
 *  3e30402f-c6c9-8132), and JSON.stringify on a nested value is raw JSON on screen (bug fb007c70
 *  follow-up). Nothing here ever stringifies JSON: every level reads as words. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);
/** markets_swept -> "Markets swept". A key that is already words ("GNC UAE") keeps its casing. */
export function humanizeKey(key: string): string {
  const words = key.replaceAll("_", " ").replace(/\s+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}
/** One value on one line: primitives as-is, arrays comma-separated, objects as "Key: value; Key: value". */
export function inlineFactValue(value: unknown): string {
  if (value === null || value === undefined) return GAP_MARK;
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (Array.isArray(value)) return value.map((item) => (isRecord(item) || Array.isArray(item) ? `(${inlineFactValue(item)})` : inlineFactValue(item))).join(", ");
  if (isRecord(value)) return Object.entries(value).map(([key, item]) => `${humanizeKey(key)}: ${inlineFactValue(item)}`).join("; ");
  return String(value);
}
/** Link for a lead's Intercom thread. The ref may be a bare conversation id or a full Intercom URL pasted into Reply; a full URL is used as-is (never prefixed twice). */
export function intercomUrl(ref: string | null | undefined): string | null {
  const value = ref?.trim(); if (!value) return null;
  if (/^https?:\/\//i.test(value)) { try { const url = new URL(value); return url.hostname === "intercom.com" || url.hostname.endsWith(".intercom.com") ? url.toString() : null; } catch { return null; } }
  return `https://app.intercom.com/a/inbox/_/inbox/conversation/${encodeURIComponent(value)}`;
}
export type FactLine = { key: string; value: string; sub?: Array<{ key: string; value: string }> };
/** A top-level object fact as one line per key; a nested object becomes a one-level sub-list. */
export function factLines(value: unknown): FactLine[] | null {
  if (!isRecord(value)) return null;
  return Object.entries(value).map(([key, item]) => isRecord(item)
    ? { key: humanizeKey(key), value: "", sub: Object.entries(item).map(([subKey, subItem]) => ({ key: humanizeKey(subKey), value: inlineFactValue(subItem) })) }
    : { key: humanizeKey(key), value: inlineFactValue(item) });
}
/** Plain-text form of a fact: scalars as-is, arrays comma-separated, objects one "Key: value" per line. */
export function formatFactValue(value: unknown): string | number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number") return value;
  const lines = factLines(value);
  if (lines) return lines.map((line) => line.sub ? [`${line.key}:`, ...line.sub.map((sub) => `  ${sub.key}: ${sub.value}`)].join("\n") : `${line.key}: ${line.value}`).join("\n");
  return inlineFactValue(value);
}
export function partialPeriodLabel(liveSince?: string | null, closed = false): string {
  if (closed) return "Closed period";
  return liveSince ? `live since ${liveSince}: no closed period has completed yet: a gap, never a zero` : "No closed period has completed yet: a gap, never a zero";
}
export function slaClock(slaDueAt: string | null, now = new Date()): string {
  if (!slaDueAt) return `${GAP_MARK} No SLA data`;
  const difference = now.getTime() - new Date(slaDueAt).getTime(); const absoluteHours = Math.max(0, Math.floor(Math.abs(difference) / 3_600_000)); const days = Math.floor(absoluteHours / 24); const hours = absoluteHours % 24;
  return difference >= 0 ? `${days} d ${hours} h past the 48 hour reply window` : `${days} d ${hours} h on the clock`;
}
export const publishedReadouts = (items: Readout[]) => items.filter((item) => item.published_at !== null);
export const missingGateInputs = (calc: MarginCalc) => calc.cost_inputs.filter((input) => input.flag === "missing").map((input) => input.component);
export function latestWeek(periods: WeeklyPeriod[] | null | undefined): WeeklyPeriod | null {
  if (!periods?.length) return null;
  return [...periods].sort((a, b) => b.period_key.localeCompare(a.period_key))[0];
}
export const NEEDS_MESSAGES: Readonly<Record<string, string>> = {
  NEEDS_REASON: "A reason is required", NEEDS_CAP_SLOT: "Three channels are already in flight. Park one to start another.", NEEDS_CHECKLIST: "The preparation checklist is still open.", NEEDS_APPROVED_MARGIN: "Prepare to Activate needs an approved margin gate", NEEDS_FACTS: "Scoring opens once each research fact is recorded, or marked unknown with a reason", NEEDS_CLOSED_PERIOD: "Only a closed period can be recorded", NEEDS_POSITIVE_MARGIN: "This gate cannot be approved while margin is at or below zero", NEEDS_ASSESSMENT: "Assess this lead first: a lead moves past Application only with an assessment or a recorded unknown", NEEDS_CURRENT_FACT: "A research fact on this record is out of date; record it again before scoring", NEEDS_NODE_UNUSED: "This category is still in use and cannot be retired",
};
export const haltMessage = (halt: string, detail = "") => NEEDS_MESSAGES[halt] ?? (detail || `Action paused: ${halt}`);
/** The API's own reason ({"detail": "..."}), when it sent one as text. */
export function apiDetail(body: unknown): string | null {
  const detail = isRecord(body) ? body.detail : null;
  return typeof detail === "string" && detail.trim() ? detail.trim() : null;
}
/** 401 = not signed in; 403 = signed in but not on the funnel's access list. Kept apart so the reader knows which fix applies. */
export function statusMessage(status: number, body?: unknown): string {
  if (status === 401) return "You are not signed in to the funnel API, or your sign-in expired. Reload the page to sign in again.";
  if (status === 403) { const detail = apiDetail(body); return `Your account is signed in but the funnel API does not allow it${detail ? ` (${detail})` : ""}. Ask the Customer Acquisition area owner to add you to the funnel's access list.`; }
  if (status === 404) return "This record does not exist.";
  if (status === 503) return "The funnel API could not be reached, so nothing is shown. Try again shortly.";
  return "Something went wrong. Try again.";
}
export function formatDate(value: string | null | undefined): string {
  if (!value) return `${GAP_MARK} No data`; const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toISOString().slice(0, 10);
}

export type ChipTone = "purple" | "pass" | "warn" | "fail" | "gray";
export type ChipSpec = { tone: ChipTone; label: string };
const titleCase = (value: string) => value.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
/** Prototype phase chips: filled dot + purple for any in-flight phase, hollow dot + gray for backlog. */
export function phaseChip(phase: string): ChipSpec {
  return phase === "backlog" ? { tone: "gray", label: "○ Backlog" } : { tone: "purple", label: `● ${titleCase(phase)}` };
}
/** high / medium read as pass, low / speculative as warn, unknown as gray. */
export function certaintyTone(certainty: string | null | undefined): ChipTone {
  if (certainty === "high" || certainty === "medium") return "pass";
  if (certainty === "low" || certainty === "speculative") return "warn";
  return "gray";
}
export function replyStateChip(replyState: string | null | undefined): ChipSpec {
  if (replyState === "replied") return { tone: "pass", label: "Replied" };
  if (replyState === "unreplied") return { tone: "warn", label: "Unreplied" };
  if (!replyState || replyState === "none") return { tone: "gray", label: "No reply yet" };
  return { tone: "gray", label: replyState };
}
export function gateChip(status: MarginStatus | MarginCalc["status"]): ChipSpec {
  if (status === "pending") return { tone: "fail", label: "⏳ Waiting on you" };
  if (status === "blocked_pending_inputs") return { tone: "warn", label: "Blocked: pending inputs" };
  if (status === "approved") return { tone: "pass", label: "Approved" };
  if (status === "rejected") return { tone: "fail", label: "Rejected" };
  return { tone: "gray", label: "No margin calc" };
}
export function factsChip(recorded: number, required: number): ChipSpec {
  // The denominator is the engine's required-fact count, never a literal: the module's old
  // hardcoded four had zero overlap with the six scoring actually asks for, so the chip and
  // the Scoring panel could never agree (bug 81fd AC2).
  return { tone: required > 0 && recorded >= required ? "pass" : "warn", label: `${recorded} of ${required} recorded` };
}
export type NeedsSeverity = "red" | "amber" | "purple";
export function needsSeverity(kind: NeedsYouRow["kind"]): NeedsSeverity {
  if (kind === "gates_waiting" || kind === "overdue_leads") return "red";
  if (kind === "pending_tree_proposals") return "purple";
  return "amber";
}
/** Value cell of the gate calculation: a missing input is a gap, never 0; currency rides after the number. */
export function costInputValue(input: CostInput): string {
  if (input.flag === "missing" || input.value === null || input.value === undefined) return GAP_MARK;
  return input.currency ? `${input.value} ${input.currency}` : String(input.value);
}

export function latestMonth(periods: MonthlyPeriod[] | null | undefined): MonthlyPeriod | null {
  if (!periods?.length) return null;
  return [...periods].sort((a, b) => b.period_key.localeCompare(a.period_key))[0];
}
export type ChannelTableRow = { channel_id: string; channel_name: string; week: WeeklyChannelRow | null; month: MonthlyChannelRow | null };
/** One row per channel seen in the closed week or the closed month: week rows first (API order), month-only channels appended. An absent side stays null; nothing is invented. */
export function channelTableRows(week: WeeklyPeriod | null, month: MonthlyPeriod | null): ChannelTableRow[] {
  const rows: ChannelTableRow[] = (week?.channels ?? []).map((w) => ({ channel_id: w.channel_id, channel_name: w.channel_name, week: w, month: month?.channels.find((m) => m.channel_id === w.channel_id) ?? null }));
  for (const m of month?.channels ?? []) if (!rows.some((row) => row.channel_id === m.channel_id)) rows.push({ channel_id: m.channel_id, channel_name: m.channel_name, week: null, month: m });
  return rows;
}
/** A percentage the API carries, or null; the caller renders null as a gap. */
/** One decimal, always (prototype v4: "13.5%", "4.0%"); the API serves share_pct / wow_pct as raw floats. */
export function pct1(value: number): string {
  return (Math.round(value * 10) / 10).toFixed(1);
}

export function sharePct(value: number | null | undefined): string | null {
  return value === null || value === undefined || !Number.isFinite(value) ? null : `${pct1(value)}%`;
}
/** One entry of the ranked list after marketplace grouping: a row as the server sent it, or the
 *  channels that share a sub-group branch (e.g. "Amazon › Amazon EU") collapsed into one entry. */
export type RankedEntry<R, C> = { kind: "row"; row: R } | { kind: "group"; key: string; label: string; channels: C[] };
/** Group channel rows whose branch path has 2+ segments by that full path. The group sits where
 *  its first member sorts; everything else keeps its server order. Only what is on the page is
 *  grouped — nothing is fetched to complete a group. */
export function groupRankedRows<R, C extends R & Pick<Opportunity, "branch_path">>(rows: R[], isChannel: (row: R) => row is C): Array<RankedEntry<R, C>> {
  const entries: Array<RankedEntry<R, C>> = []; const groups = new Map<string, { key: string; label: string; channels: C[] }>();
  for (const row of rows) {
    if (!isChannel(row)) { entries.push({ kind: "row", row }); continue; }
    const segments = (row.branch_path ?? "").split("›").map((segment) => segment.trim()).filter(Boolean);
    if (segments.length < 2) { entries.push({ kind: "row", row }); continue; }
    const key = segments.join(" › "); const existing = groups.get(key);
    if (existing) { existing.channels.push(row); continue; }
    const group = { key, label: segments[segments.length - 1], channels: [row] }; groups.set(key, group); entries.push({ kind: "group", ...group });
  }
  return entries;
}

export type SortColumn = "market" | "est_month" | "year" | "certainty" | "difficulty" | "stage" | "priority";
export type ColumnSort = { column: SortColumn; dir: "asc" | "desc" } | null;
type SortKey = string | number | null;

const CERTAINTY_ASC = ["speculative", "low", "medium", "high"];
const EFFORT_ASC = ["S", "M", "L", "XL"];
const PHASE_ASC = ["backlog", "qualify", "prepare", "activate", "live"];
const STAGE_ASC = ["identified", "warm", "application", "contract", "account_setup", "live"];
const ordinal = (scale: string[], value: string | null | undefined): SortKey => { const i = value ? scale.indexOf(value) : -1; return i < 0 ? null : i; };

export function nextColumnSort(current: ColumnSort, column: SortColumn): ColumnSort {
  if (!current || current.column !== column) return { column, dir: "asc" };
  return current.dir === "asc" ? { column, dir: "desc" } : null;
}

function compareKeys(a: SortKey, b: SortKey, dir: "asc" | "desc"): number {
  if (a == null || b == null) return a == null ? (b == null ? 0 : 1) : -1;
  const raw = typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
  return dir === "asc" ? raw : -raw;
}

function leadKey(lead: LeadRow, column: SortColumn): SortKey {
  switch (column) {
    case "market": return lead.country?.trim() || null;
    case "est_month": return lead.est_units_month_base;
    case "year": return lead.est_units_year;
    case "certainty": return ordinal(CERTAINTY_ASC, lead.certainty);
    case "difficulty": { const effort = ordinal(EFFORT_ASC, lead.setup_effort_band); return lead.engagement_difficulty == null ? null : lead.engagement_difficulty * 10 + (typeof effort === "number" ? effort : 0); }
    case "stage": return ordinal(STAGE_ASC, lead.lead_stage);
    case "priority": return lead.priority_score;
  }
}

/** Assessed leads sorted by the column (stable, blanks last); unassessed and unknown leads follow in server order. */
export function sortLeads(leads: LeadRow[], sort: NonNullable<ColumnSort>): LeadRow[] {
  const assessed = leads.filter((lead) => lead.assessment_state === "assessed");
  const rest = leads.filter((lead) => lead.assessment_state !== "assessed");
  return [...assessed.sort((a, b) => compareKeys(leadKey(a, sort.column), leadKey(b, sort.column), sort.dir)), ...rest];
}

function channelKey(channel: ChannelRow, sort: NonNullable<ColumnSort>): SortKey {
  if (sort.column === "market") return channel.market?.trim() || null;
  if (sort.column === "year") return channel.pipeline_potential || null;
  if (sort.column === "stage") return ordinal(PHASE_ASC, channel.phase);
  const first = channel.leads?.[0];
  return first && first.assessment_state === "assessed" ? leadKey(first, sort.column) : null;
}

const isChannelRow = (row: ChannelRow | LeadRow): row is ChannelRow => "phase" in row;

/** Header sort over ONE loaded page (C6 v1.3). Never renumbers; null sort returns the rows untouched. */
export function sortRankedRows(rows: Array<ChannelRow | LeadRow>, sort: ColumnSort): Array<ChannelRow | LeadRow> {
  if (!sort) return rows;
  const channels = rows.filter(isChannelRow).map((channel) => channel.leads ? { ...channel, leads: sortLeads(channel.leads, sort) } : channel);
  const keyed = channels.map((channel) => ({ channel, key: channelKey(channel, sort) }));
  keyed.sort((a, b) => compareKeys(a.key, b.key, sort.dir));
  const leads = rows.filter((row): row is LeadRow => !isChannelRow(row));
  return [...keyed.map((entry) => entry.channel), ...sortLeads(leads, sort)];
}
